/**
 * Keep `--app-height` in sync with the *visible* Mini App sheet.
 *
 * Telegram's `--tg-viewport-stable-height` does not update while the user
 * pulls the compact (~70%) sheet open (and on iOS often never updates after
 * the gesture). A CSS-only layout then stays short and the footer sits in
 * the middle / off-screen. We write the live height onto the document on
 * every viewport event.
 *
 * `viewportHeight` also stays stale after a menu-button open and when the
 * Telegram Desktop window is scaled. Once the sheet is stable, follow the
 * live iframe size when it has grown past that stale value.
 */
const DESKTOP_PLATFORMS = new Set([
  "tdesktop",
  "macos",
  "web",
  "weba",
  "unigram",
]);

let sheetDragging = false;

function visibleFrameHeight(): number {
  return Math.round(
    window.visualViewport?.height || window.innerHeight || 0
  );
}

export function syncTelegramViewportHeight(): void {
  const webApp = window.Telegram?.WebApp;
  const telegramHeight = Math.round(webApp?.viewportHeight || 0);
  const frameHeight = visibleFrameHeight();
  const desktop = DESKTOP_PLATFORMS.has(webApp?.platform ?? "");

  let height = 0;
  if (desktop) {
    height = frameHeight || telegramHeight;
  } else if (sheetDragging && telegramHeight > 0) {
    height = telegramHeight;
  } else if (frameHeight > 0) {
    height = Math.max(telegramHeight, frameHeight);
  } else {
    height = telegramHeight || Math.round(webApp?.viewportStableHeight || 0);
  }
  if (height <= 0) return;

  const root = document.documentElement;
  root.style.setProperty("--app-height", `${height}px`);
  root.style.height = `${height}px`;
  document.body.style.height = `${height}px`;
  const appRoot = document.getElementById("root");
  if (appRoot) {
    appRoot.style.height = `${height}px`;
  }

  const safeBottom = Math.max(
    webApp?.safeAreaInset?.bottom ?? 0,
    webApp?.contentSafeAreaInset?.bottom ?? 0
  );
  root.style.setProperty("--app-safe-bottom", `${safeBottom}px`);
}

export function subscribeTelegramViewport(onChange?: () => void): () => void {
  let pollId: ReturnType<typeof setInterval> | undefined;

  const handle = (...args: unknown[]) => {
    const event = args[0] as { isStateStable?: boolean } | undefined;
    if (event?.isStateStable === false) {
      sheetDragging = true;
    } else if (event?.isStateStable === true) {
      sheetDragging = false;
    }

    syncTelegramViewportHeight();
    onChange?.();
    requestAnimationFrame(() => {
      syncTelegramViewportHeight();
      onChange?.();
    });

    if (event?.isStateStable === false) {
      if (pollId !== undefined) clearInterval(pollId);
      let ticks = 0;
      pollId = setInterval(() => {
        syncTelegramViewportHeight();
        onChange?.();
        ticks += 1;
        if (ticks > 40) {
          if (pollId !== undefined) clearInterval(pollId);
          pollId = undefined;
        }
      }, 50);
    } else if (event?.isStateStable === true && pollId !== undefined) {
      clearInterval(pollId);
      pollId = undefined;
    }
  };

  handle();

  // Menu-button opens expand after the first height is captured, often
  // without another viewportChanged. Keep sampling until that settles.
  let startupTicks = 0;
  const startupPoll = setInterval(() => {
    syncTelegramViewportHeight();
    onChange?.();
    startupTicks += 1;
    if (startupTicks > 40) clearInterval(startupPoll);
  }, 50);

  const webApp = window.Telegram?.WebApp;
  webApp?.onEvent?.("viewportChanged", handle);
  webApp?.onEvent?.("safeAreaChanged", handle);
  webApp?.onEvent?.("contentSafeAreaChanged", handle);

  window.addEventListener("resize", handle);
  window.visualViewport?.addEventListener("resize", handle);
  window.visualViewport?.addEventListener("scroll", handle);

  return () => {
    clearInterval(startupPoll);
    if (pollId !== undefined) clearInterval(pollId);
    webApp?.offEvent?.("viewportChanged", handle);
    webApp?.offEvent?.("safeAreaChanged", handle);
    webApp?.offEvent?.("contentSafeAreaChanged", handle);
    window.removeEventListener("resize", handle);
    window.visualViewport?.removeEventListener("resize", handle);
    window.visualViewport?.removeEventListener("scroll", handle);
  };
}
